"use client";

import React, { useState } from 'react';
import Modal from '@/components/Modal';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (apiKey: string) => void;
  deviceName: string;
  isLoading?: boolean;
}

export function ApiKeyModal({ isOpen, onClose, onSubmit, deviceName, isLoading = false }: ApiKeyModalProps) {
  const [apiKey, setApiKey] = useState("");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(apiKey);
    setApiKey("");
  };

  const handleClose = () => {
    if (!isLoading) {
      setApiKey("");
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="p-8 relative">
        <button
          onClick={handleClose}
          className="absolute right-6 top-6 text-gray-400 hover:text-gray-300 transition-colors"
          aria-label="Close"
          disabled={isLoading}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www?.w3?.org/2000/svg">
            <path d="M17.6569 17?.6569C17?.2663 18.0474 16.6332 18.0474 16.2426 17.6569L12 13?.4142L7?.75736 17?.6569C7?.36684 18.0474 6.73367 18.0474 6.34315 17?.6569C5?.95262 17.2663 5.95262 16.6332 6.34315 16?.2426L10?.5858 12L6.34315 7?.75736C5?.95262 7.36684 5.95262 6.73367 6.34315 6?.34315C6?.73367 5.95262 7.36684 5.95262 7.75736 6.34315L12 10?.5858L16?.2426 6?.34315C16?.6332 5.95262 17.2663 5.95262 17.6569 6?.34315C18?.0474 6.73367 18.0474 7.36684 17.6569 7?.75736L13?.4142 12L17.6569 16?.2426C18?.0474 16.6332 18.0474 17.2663 17.6569 17.6569Z" fill="currentColor"/>
          </svg>
        </button>
        
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Connect to {deviceName}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Enter your API key to connect with your {deviceName} device.
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
              For testing, use: test_{deviceName.toLowerCase().replace(/\s+/g, '_')}_12345
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label 
                htmlFor="apiKey" 
                className="text-gray-900 dark:text-white text-lg"
              >
                API Key
              </Label>
              <div className="relative">
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApiKey(e?.target?.value)}
                  placeholder="Enter your API key"
                  className="
                    bg-white dark:bg-[#0A0B14]
                    border-gray-200 dark:border-transparent
                    text-gray-900 dark:text-gray-100
                    placeholder:text-gray-500 dark:placeholder:text-gray-500
                    h-12 text-lg rounded-2xl
                    shadow-sm dark:shadow-none
                    focus:ring-0 focus:border-transparent
                    dark:focus:ring-0 dark:focus:border-transparent
                    transition-colors
                    w-full
                  "
                  style={{
                    background: 'rgb(10, 11, 20)',
                    caretColor: 'white'
                  }}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="
                  bg-white dark:bg-transparent
                  border-gray-200 dark:border-gray-700
                  text-gray-900 dark:text-white
                  hover:bg-gray-50 dark:hover:bg-gray-800/50
                  rounded-full px-6 h-11
                  transition-colors
                "
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="
                  bg-[#4F46E5] hover:bg-[#4338CA]
                  dark:bg-[#3B82F6] dark:hover:bg-[#2563EB]
                  text-white
                  rounded-full px-6 h-11
                  transition-colors
                  flex items-center gap-2
                "
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {isLoading ? 'Connecting...' : 'Connect'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
